import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { t, Trans } from 'src/translations'

import { ChevronLeftIcon } from '@toasttab/buffet-pui-icons'

import {
  PayForCheckInput,
  usePayForCheckMutation
} from 'src/apollo/onlineOrdering'
import { ButtonType, useBookingQuery } from 'src/apollo/sites'
import { reportError } from 'src/lib/js/clientError'
import { useOOClient } from 'src/shared/components/common/oo_client_provider/OOClientProvider'

import Image from 'shared/components/common/Image'
import { BrandedButton } from 'shared/components/common/button'

import CreditCardForm, {
  EncryptedCreditCardData
} from 'public/components/default_template/online_ordering/checkout/payment/CreditCardForm'
import { CartContextProvider } from 'public/components/online_ordering/CartContext'
import { PaymentContextProvider } from 'public/components/online_ordering/PaymentContext'

import { Countdown } from './Countdown'
import ReservationOverview from './ReservationOverview'
import {
  SeatingLocation,
  Deposit,
  timeInFutureInUTC,
  useReservationCancellation,
  type OnReservationCancellation
} from './reservationUtils'

type DepositPaymentProps = {
  restaurantGuid: string
  reservationGuid: string
  numGuests: number
  serviceArea: SeatingLocation | null
  selectedDate: Date | null
  selectedTime: Date | null
  deposit?: Deposit | null
  changeSelection?: any
  timezone: string
  occasion: string
  onCancellation: OnReservationCancellation
  step: number
  creditCardData?: EncryptedCreditCardData | null
  setCreditCardData: (arg0: EncryptedCreditCardData | null) => void
  onDepositPaid: () => void
  email: string
}

type ValidatedDepositData = Pick<
  PayForCheckInput,
  'restaurantGuid' | 'email' | 'checkGuid' | 'orderGuid' | 'newCardInput'
>

const DepositPayment = ({
  restaurantGuid,
  reservationGuid,
  numGuests,
  serviceArea,
  selectedDate,
  selectedTime,
  changeSelection,
  timezone,
  occasion,
  deposit,
  onCancellation,
  step,
  creditCardData,
  setCreditCardData,
  onDepositPaid,
  email
}: DepositPaymentProps) => {
  const { cancel } = useReservationCancellation(
    reservationGuid,
    restaurantGuid,
    onCancellation,
    false
  )
  const [paymentError, setPaymentError] = useState<string | null>(null)

  const { data: bookingData } = useBookingQuery({
    variables: {
      bookingGuid: reservationGuid,
      restaurantGuid: restaurantGuid
    },
    skip: !reservationGuid || !restaurantGuid,
    ssr: false
  })

  const validatedPaymentInput: ValidatedDepositData | null = useMemo(() => {
    const checkGuid = bookingData?.booking?.depositCheckId
    const orderGuid = bookingData?.booking?.depositOrderId
    if (checkGuid && creditCardData && email && orderGuid && restaurantGuid) {
      return {
        restaurantGuid,
        email,
        checkGuid,
        orderGuid,
        newCardInput: creditCardData
      }
    }
    return null
  }, [bookingData, email, restaurantGuid, creditCardData])

  const [payForCheckMutation, { loading: submittingPayment }] =
    usePayForCheckMutation({ client: useOOClient() })

  const submitPayment = useCallback(
    async (input: ValidatedDepositData): Promise<true> => {
      try {
        const res = await payForCheckMutation({
          variables: {
            input: {
              ...input,
              tipAmount: 0
            }
          }
        })
        if (res.data?.payForCheck?.__typename === 'PayForCheckError') {
          throw res.data.payForCheck
        }
      } catch (err) {
        throw new Error(
          'Error encountered while submitting deposit payment information. ',
          { cause: err }
        )
      }
      return true
    },
    [payForCheckMutation]
  )

  const payReservationDeposit = useCallback(async () => {
    if (!validatedPaymentInput) {
      // notify the customer that their info isn't valid.
      setPaymentError('Please complete all required fields.')
      return
    }
    let paymentSuccessful
    try {
      paymentSuccessful = await submitPayment(validatedPaymentInput)
    } catch (err) {
      if ('message' in err) {
        setPaymentError(err.message as string)
      } else {
        reportError(err)
        setPaymentError('Payment failed.')
      }
    }
    if (paymentSuccessful) {
      onDepositPaid()
    } else {
      reportError('Reservation deposit payment failed')
      setPaymentError('An unknown error occurred. Please try again later.')
    }
  }, [submitPayment, onDepositPaid, validatedPaymentInput])

  return (
    <div className='depositPayment' data-testid='deposit-payment'>
      <div className='currentSelection'>
        <ReservationOverview
          numGuests={numGuests}
          selectedDate={selectedDate}
          selectedTime={selectedTime}
          serviceArea={serviceArea}
          timezone={timezone}
          occasion={occasion}
        />
      </div>
      <div>
        <hr className='menuDividingLine menuDividingLineMarginTop' />
        <div className='depositDescription'>
          <p className='depositDescriptionTitle'>
            {t('deposit-payment.reservation-deposit')}
          </p>
          {deposit?.strategyType === 'ByBooking' ? (
            <div>
              {deposit && deposit.actualAmount && (
                <p className='depositMessage'>
                  {t('deposit-payment.deposit-per-booking', {
                    amount: formatPrice(deposit.actualAmount)
                  })}
                </p>
              )}
              <div className='depositPaymentContainer'>
                <p className='depositMessage'>
                  {t('deposit-payment.reservation-x1')}
                </p>
                {deposit && deposit.actualAmount && (
                  <p className='depositMessage'>
                    ${formatPrice(deposit.actualAmount)}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div>
              {deposit && deposit.amountPerGuest && (
                <p
                  className='depositMessage'
                  data-testid='per-person-deposit-disclosure'
                >
                  <Trans
                    i18nKey='deposit-payment.require-deposit-per-person'
                    values={{ amount: formatPrice(deposit.amountPerGuest) }}
                    components={{ span: <span /> }}
                  />
                </p>
              )}
              <div className='depositPaymentContainer'>
                <p className='depositMessage'>
                  {t('deposit-payment.reservation-x-guests', {
                    count: numGuests
                  })}
                </p>
                {deposit && deposit.actualAmount && (
                  <p className='depositMessage'>
                    ${formatPrice(deposit.actualAmount)}
                  </p>
                )}
              </div>
            </div>
          )}
          <hr className='menuDividingLine menuDividingLinePaddingBottom' />
          <div className='depositPaymentContainer'>
            <p className='depositAmountDue'>
              {t('deposit-payment.amount-due')}
            </p>
            {deposit && deposit.actualAmount && (
              <p className='depositAmountDue' data-testid='amount-due'>
                ${deposit.actualAmount.toFixed(2)}
              </p>
            )}
          </div>
          {deposit?.cancellationRefundableTimeframe !== undefined &&
          deposit.cancellationRefundableTimeframe > 0 ? (
            <p className='depositMessage'>
              <Trans
                i18nKey='deposit-payment.refundable-policy'
                values={{ hours: deposit.cancellationRefundableTimeframe }}
                components={{ Bold: <b /> }}
              />
            </p>
          ) : deposit?.cancellationRefundableTimeframe === -1 ? (
            <p className='depositMessage'>
              {t('deposit-payment.non-refundable')}
            </p>
          ) : (
            <p className='depositMessage'>
              {t('deposit-payment.refundable-until-reservation')}
            </p>
          )}
        </div>
        {paymentError && <DepositError errorMessage={paymentError} />}
        <CartContextProvider>
          {/* Paying deposits doesnt currently use SPI. If this changes, we'll need to reoslve the issue fixed by https://github.toasttab.com/toasttab/toast-sites-web/pull/3049 */}
          <PaymentContextProvider skipPaymentIntent>
            <CreditCardForm onCardChange={setCreditCardData} />
          </PaymentContextProvider>
        </CartContextProvider>
        <p className='countdown'>
          <Trans
            i18nKey='deposit-payment.holding-reservation-for'
            components={{
              Countdown: (
                <Countdown
                  expirationDate={timeInFutureInUTC(
                    deposit?.autoCancelUnpaidDeposit
                      ? deposit.autoCancelUnpaidDepositTimeframe ?? 15
                      : 15
                  )}
                  reservationGuid={reservationGuid}
                  restaurantGuid={restaurantGuid}
                  step={step}
                  onCancellation={onCancellation}
                />
              )
            }}
          />
        </p>
      </div>
      <span className='findTableButton'>
        <div
          className='backButton'
          onClick={() => {
            changeSelection()
            cancel()
          }}
        >
          <ChevronLeftIcon accessibility='decorative' />
          <button type='button' className='changeSelection'>
            {t('deposit-payment.back')}
          </button>
        </div>
        <BrandedButton
          variant={ButtonType.Primary}
          disabled={submittingPayment || !validatedPaymentInput}
          onClick={() => {
            payReservationDeposit()
          }}
        >
          {t('deposit-payment.complete-booking')}
        </BrandedButton>
      </span>
    </div>
  )
}

type DepositErrorProps = {
  errorMessage: string
}

const formatPrice = (amount: number) =>
  amount % 1 !== 0 ? amount?.toFixed(2) : amount

const DepositError = ({ errorMessage }: DepositErrorProps) => {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    // Scroll to this error on first render.
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [])
  return (
    <div className='depositError' ref={ref}>
      <Image
        className='warning-icon'
        alt='Warning icon'
        src='icons/warning-red.svg'
      />
      <div>
        <p>
          <strong>{t('deposit-payment.payment-unsuccessful')}</strong>
        </p>
        <p>{errorMessage}</p>
      </div>
    </div>
  )
}

export default DepositPayment
